/**
 * Unified helper for opening server-generated PDFs in a new browser tab.
 *
 * The browser pop-up blocker requires window.open() to be called synchronously
 * within a user click. So instead of downloading a blob (which requires await),
 * we rely on the backend supporting `?token=...` and let the browser fetch the
 * PDF directly in the new tab.
 *
 * All print endpoints in this app (server.py + umrah_routes.py) accept either
 * an Authorization header OR a `token` query parameter.
 *
 * Usage:
 *   openServerPdf(`${API}/daily-operations/${id}/print`)
 *   openServerPdf(`${API}/reports/daily-operations/print`, {start_date: '2025-01-01', end_date: '2025-12-31'})
 */

export function openServerPdf(baseUrl, params = {}) {
  const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') || '';
  const usp = new URLSearchParams();
  if (token) usp.set('token', token);
  // Cache-buster: prevents the browser from serving a stale PDF after a deploy.
  // Combined with the backend's `Cache-Control: no-store`, this guarantees the
  // user always gets a freshly-rendered PDF using the latest template version.
  usp.set('_cb', String(Date.now()));
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    usp.set(k, String(v));
  });
  const sep = baseUrl.includes('?') ? '&' : '?';
  const url = `${baseUrl}${sep}${usp.toString()}`;

  // Synchronous → not blocked by popup blocker (user-gesture preserved)
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    // Fallback: navigate via hidden anchor click
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  return win;
}

/**
 * Force-download a server PDF (rare — only when the user explicitly wants the
 * file saved instead of preview-printed). Builds the same URL with a token.
 */
export function downloadServerPdf(baseUrl, filename = 'document.pdf', params = {}) {
  const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') || '';
  const usp = new URLSearchParams();
  if (token) usp.set('token', token);
  // Cache-buster: same rationale as openServerPdf.
  usp.set('_cb', String(Date.now()));
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    usp.set(k, String(v));
  });
  const sep = baseUrl.includes('?') ? '&' : '?';
  const url = `${baseUrl}${sep}${usp.toString()}`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}


/**
 * Open a print URL — user picks format via a styled Shadcn Dialog.
 *
 * Previously this used `window.confirm`, which Chrome silently blocks in
 * some contexts (e.g., inside a Dialog/Modal, or after multiple consecutive
 * confirms). That caused the "🖨️ طباعة الوصل" button in trip-payment
 * details to do nothing on click.
 *
 * Now we dispatch a custom event picked up by <FormatPickerDialog/>
 * (mounted once at the app root). The dialog calls __resolveFormatPick
 * with the chosen format, which then synchronously opens the PDF/XLSX —
 * still preserving the user-gesture (because the dialog button click is
 * itself a user gesture).
 */
let __pendingFormatRequest = null;

export function askPdfOrXlsx(baseUrl, params = {}) {
  // If a previous request is still hanging (user closed without picking),
  // cancel it so we don't leak resolves.
  if (__pendingFormatRequest) {
    __pendingFormatRequest = null;
  }
  return new Promise((resolve) => {
    __pendingFormatRequest = (format) => {
      __pendingFormatRequest = null;
      if (format) {
        openPdfOrXlsx(baseUrl, format, params);
      }
      resolve(format);
    };
    // Defer dispatch so React state in the dialog can react in the next tick.
    window.dispatchEvent(new CustomEvent('ask-pdf-format'));
  });
}

/**
 * Internal — called by <FormatPickerDialog/> when the user picks a format
 * or cancels (format === null).
 */
export function __resolveFormatPick(format) {
  if (typeof __pendingFormatRequest === 'function') {
    __pendingFormatRequest(format);
  }
}

/**
 * Open a server print URL in the chosen format.
 * @param {string} baseUrl - e.g. `${API}/daily-operations/${id}/print`
 * @param {'pdf'|'xlsx'} format
 * @param {object} params  - extra query params
 */
export function openPdfOrXlsx(baseUrl, format = 'pdf', params = {}) {
  const finalParams = { ...params, format };
  if (format === 'xlsx') {
    // XLSX must download (browsers won't preview xlsx inline). Use anchor.
    const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') || '';
    const usp = new URLSearchParams();
    if (token) usp.set('token', token);
    usp.set('_cb', String(Date.now()));
    Object.entries(finalParams).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      usp.set(k, String(v));
    });
    const sep = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${sep}${usp.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return null;
  }
  // PDF → open in new tab (preview + print)
  return openServerPdf(baseUrl, finalParams);
}

